// /products — My Products library.
//
// User-facing: paste a product URL, the backend scrapes name + short
// description + image (same pipeline as the wizard's StepUpload URL
// path) and persists the result. Saved products list below in a 3-col
// grid (image / name / description / delete). Cap at 10 products per
// user — enforced server-side, the Add input goes disabled with a
// "limit reached" hint when at capacity.
//
// Phase 2 (future, NOT now) — surface this library inside the wizard's
// Upload step so users can re-use a saved product across multiple
// generations without re-scraping.

import { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  productLimitForTier,
  type UserProduct,
  type Collection,
  addProductsFromUrl,
  uploadProduct,
  ProductNeedsInfoError,
  deleteProduct,
  listProducts,
  clearAllProducts,
  syncMoreProducts,
  getProductSaveJobStatus,
  listCollections,
  createCollection,
  deleteCollection,
  listCollectionProducts,
  addProductsToCollection,
  removeProductFromCollection,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';

type ActiveTab = 'products' | 'collections';


export default function ProductsPage() {
  const confirm = useConfirm();
  const { activeBrand } = useBrand();
  const { planTier } = useCredits();
  // Per-brand product cap follows the plan tier (Pro & above = full catalog).
  const productCap = productLimitForTier(planTier);
  // --- Saved products list ---
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  // --- Clear-all in-flight so the Clear button shows a spinner ---
  const [clearing, setClearing] = useState(false);

  // --- Sync-more job tracking ---
  // syncing: button shows spinner + label flips to "Syncing…"
  // syncMessage: success/error banner about the most recent sync run
  // (e.g. "Synced 12 new products" / "You're already up to date").
  // syncJobId: when set, page polls /save-batch/status every 4s so new
  // products trickle in without a manual refresh.
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncJobId, setSyncJobId] = useState<string | null>(null);

  // --- Tabs + search ---
  // activeTab toggles between the All-Products view (default) and the
  // Collections view. Search filters whatever is currently rendered:
  // products by name/description/category/tags, collections by name.
  const [activeTab, setActiveTab] = useState<ActiveTab>('products');
  const [search, setSearch] = useState('');

  // Masonry column count — matches the gallery / analytics pattern.
  // CSS Grid with `items-start` was giving wildly uneven tile sizes
  // because a card with a longer description sized its row and left the
  // shorter siblings with huge empty space. Flex-column round-robin
  // packs each column independently — every card sits flush against
  // the previous one in its column.
  const computeProductColumnCount = (): number => {
    if (typeof window === 'undefined') return 5;
    const w = window.innerWidth;
    if (w >= 1536) return 5;        // 2xl
    if (w >= 1024) return 4;        // lg/xl
    if (w >= 768) return 3;         // md
    if (w >= 640) return 2;         // sm
    return 2;                       // mobile minimum
  };
  const [productColumns, setProductColumns] = useState<number>(computeProductColumnCount);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = computeProductColumnCount();
        setProductColumns((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // --- Collections state ---
  // collections: brand-scoped list, sorted by updated_at desc by the API.
  // openCollection: when non-null, the Collections tab drills down into
  //   the products grid for that collection (back button restores the list).
  // openCollectionProducts: cached product list for the open collection.
  // openCollectionLoading: first-load spinner for the drill-down view.
  // creatingCollection: form state for the inline "Create new" card.
  // addToTarget: which product is currently in the "Add to which collection?"
  //   modal flow (null = modal closed).
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [openCollection, setOpenCollection] = useState<Collection | null>(null);
  const [openCollectionProducts, setOpenCollectionProducts] = useState<UserProduct[]>([]);
  const [openCollectionLoading, setOpenCollectionLoading] = useState(false);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDesc, setNewCollectionDesc] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [addToTarget, setAddToTarget] = useState<UserProduct | null>(null);

  // --- URL-fetch state ---
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Success toast after a successful add. Different channel from
  // fetchError so a successful collection-URL add (which may still have
  // a skipped count) shows the positive "Added N" message instead of
  // a warning-styled banner.
  const [fetchNotice, setFetchNotice] = useState<string | null>(null);

  // --- Image-upload state ---
  // uploadBusy: an upload is in flight (the auto-classify pass runs first).
  // manualUpload: when the backend can't auto-detect a name/description from
  //   the image, it 422s with suggested values; we open a small modal so the
  //   user can confirm/edit and re-submit with the same file.
  const [uploadBusy, setUploadBusy] = useState(false);
  const [manualUpload, setManualUpload] = useState<{
    file: File;
    previewUrl: string;
    name: string;
    description: string;
    saving: boolean;
    error: string | null;
  } | null>(null);

  // --- Delete-in-flight (per product) so the spinner replaces just one row ---
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // --- Fetch active-brand's products + collections on mount + brand switch ---
  // Both lists are brand-scoped, both reset when the user switches brands.
  // openCollection is also cleared so we don't show Brand A's collection
  // detail while Brand B's products load behind it.
  useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);
    setCollectionsLoading(true);
    setOpenCollection(null);
    setOpenCollectionProducts([]);
    (async () => {
      try {
        const [existing, colls] = await Promise.all([
          listProducts(),
          listCollections().catch((err) => {
            console.error('Failed to load collections:', err);
            return [] as Collection[];
          }),
        ]);
        if (!cancelled) {
          setProducts(existing);
          setCollections(colls);
        }
      } catch (err) {
        console.error('Failed to load products:', err);
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setCollectionsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  const atCapacity = products.length >= productCap;

  // ---------- Add from URL ----------

  const handleAddFromUrl = useCallback(async () => {
    const url = fetchUrl.trim();
    if (!url) return;
    if (atCapacity) {
      setFetchError(
        `You've reached the ${productCap}-product limit. Remove one before adding another.`,
      );
      return;
    }
    setFetching(true);
    setFetchError(null);
    setFetchNotice(null);
    try {
      const result = await addProductsFromUrl(url);
      // Newest-first: the backend already returns newest-first within
      // the batch, so prepend the whole batch unchanged.
      setProducts((prev) => [...result.added, ...prev]);
      setFetchUrl('');
      // Compose a success message that's accurate for both single-URL
      // and collection-URL paths. Multi-add gets a richer message
      // ("Added N products from this collection").
      const n = result.added.length;
      if (n === 1) {
        setFetchNotice('Product added.');
      } else {
        const base = `Added ${n} products from this collection.`;
        setFetchNotice(
          result.skipped > 0 && result.skipReason
            ? `${base} (${result.skipReason})`
            : base,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch product from URL';
      setFetchError(msg);
    } finally {
      setFetching(false);
    }
  }, [fetchUrl, atCapacity]);

  // ---------- Upload from image ----------

  // First pass: upload the raw image and let the backend try to auto-detect
  // a name/description. If it can't, uploadProduct throws ProductNeedsInfoError
  // with suggested values → we open the manual-info modal instead of failing.
  const handleUploadFile = useCallback(
    async (file: File) => {
      if (atCapacity) {
        setFetchError(
          `You've reached the ${productCap}-product limit. Remove one before adding another.`,
        );
        return;
      }
      if (!file.type.startsWith('image/')) {
        setFetchError('Please choose an image file (JPG, PNG, or WebP).');
        return;
      }
      setUploadBusy(true);
      setFetchError(null);
      setFetchNotice(null);
      try {
        const result = await uploadProduct(file);
        setProducts((prev) => [...result.added, ...prev]);
        setFetchNotice('Product added.');
      } catch (err) {
        if (err instanceof ProductNeedsInfoError) {
          // Couldn't auto-detect — collect name/description from the user.
          setManualUpload({
            file,
            previewUrl: URL.createObjectURL(file),
            name: err.suggestedName ?? '',
            description: err.suggestedDescription ?? '',
            saving: false,
            error: null,
          });
        } else {
          const msg = err instanceof Error ? err.message : 'Upload failed.';
          setFetchError(msg);
        }
      } finally {
        setUploadBusy(false);
      }
    },
    [atCapacity, productCap],
  );

  // Second pass: user confirmed a name + description in the modal → re-submit
  // the same file with those values.
  const submitManualUpload = useCallback(async () => {
    setManualUpload((cur) => {
      if (!cur) return cur;
      const name = cur.name.trim();
      const description = cur.description.trim();
      if (!name || !description) {
        return { ...cur, error: 'Add both a name and a short description.' };
      }
      // Kick off the async save; we mark saving here and finish in the effect
      // below via a self-contained IIFE keyed off the current file.
      void (async () => {
        try {
          const result = await uploadProduct(cur.file, name, description);
          if (!result.added.length) throw new Error('Could not save the product.');
          setProducts((prev) => [...result.added, ...prev]);
          URL.revokeObjectURL(cur.previewUrl);
          setManualUpload(null);
          setFetchNotice('Product added.');
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Could not save the product.';
          setManualUpload((m) => (m ? { ...m, saving: false, error: msg } : m));
        }
      })();
      return { ...cur, saving: true, error: null };
    });
  }, []);

  const cancelManualUpload = useCallback(() => {
    setManualUpload((cur) => {
      if (cur) URL.revokeObjectURL(cur.previewUrl);
      return null;
    });
  }, []);

  // ---------- Delete ----------

  const handleDelete = useCallback(async (id: string) => {
    // No confirmation modal — single click delete is fine for a 10-row
    // library that's easy to re-add. If users start losing data they
    // care about, gate this behind a confirm.
    setDeletingId(id);
    try {
      await deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error('Failed to delete product:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete product';
      // Surface the failure via the same fetch-error pane (re-using the
      // existing channel keeps the UI simple).
      setFetchError(msg);
    } finally {
      setDeletingId(null);
    }
  }, []);

  // ---------- Clear all ----------

  const handleClearAll = useCallback(async () => {
    // Re-fetch the live count right before the dialog so we don't show
    // "delete 30 products" while the DB actually holds 58 (e.g. a sync-more
    // job finished between page-load and now, or another tab added rows).
    // The DELETE is unscoped — it wipes everything the server sees — so the
    // dialog needs to reflect what the server sees, not the cached list.
    let liveCount = products.length;
    try {
      const fresh = await listProducts();
      liveCount = fresh.length;
      setProducts(fresh);
    } catch (err) {
      console.error('Failed to refresh count before clear-all:', err);
      // Fall through with the cached count — user still gets a confirm
      // dialog, just with potentially-stale wording.
    }

    if (liveCount === 0) {
      setFetchNotice('Nothing to clear — your library is already empty.');
      return;
    }

    const ok = await confirm({
      title: 'Clear your entire product library?',
      message: (
        <>
          This will permanently delete <strong>{liveCount}</strong>{' '}
          saved product{liveCount === 1 ? '' : 's'} and their stored images.
          Posts and analyses that reference these products stay intact.
        </>
      ),
      confirmLabel: 'Clear all',
      cancelLabel: 'Keep them',
      destructive: true,
    });
    if (!ok) return;

    setClearing(true);
    setFetchError(null);
    setFetchNotice(null);
    try {
      const result = await clearAllProducts();
      setProducts([]);
      setFetchNotice(
        `Cleared ${result.products_deleted} product${result.products_deleted === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      console.error('Failed to clear products:', err);
      const msg = err instanceof Error ? err.message : 'Failed to clear products';
      setFetchError(msg);
    } finally {
      setClearing(false);
    }
  }, [confirm, products.length]);

  // ---------- Sync more from website ----------
  // Kicks off the async re-discover-and-append job. Backend dedupes by
  // source_url, so re-clicking is safe (no double-adds). Frontend then
  // polls the job status every 4s until done — new products trickle into
  // the grid as the worker classifies + uploads them.
  const handleSyncMore = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    setFetchError(null);
    setFetchNotice(null);
    try {
      const result = await syncMoreProducts();
      if (!result.jobId) {
        // No new products on the site — show positive nothing-to-do.
        setSyncMessage(
          `You're already up to date — ${result.discovered} products on your site, all saved.`,
        );
        setSyncing(false);
        return;
      }
      const skippedSuffix = result.skippedCount > 0
        ? ` (${result.skippedCount} would have exceeded your ${productCap}-product limit)`
        : '';
      setSyncMessage(
        `Syncing ${result.targetCount} new product${result.targetCount === 1 ? '' : 's'} from your website` +
        ` (${result.alreadySaved} already saved)${skippedSuffix}…`,
      );
      setSyncJobId(result.jobId);
    } catch (err) {
      console.error('Failed to sync more products:', err);
      const msg = err instanceof Error ? err.message : 'Failed to sync from website';
      setFetchError(msg);
      setSyncing(false);
    }
  }, []);

  // ---------- Collections handlers ----------

  // Refresh just the collections list (e.g. after add/remove/create).
  // Cheap re-fetch since the API includes pre-computed counts +
  // thumbnails — no per-collection roundtrip needed.
  const refreshCollections = useCallback(async () => {
    try {
      const fresh = await listCollections();
      setCollections(fresh);
      // If the currently-open collection still exists, refresh its
      // header info from the new list (e.g. updated thumbnails after
      // an add). Otherwise it was deleted in another tab — pop back.
      setOpenCollection((prev) => {
        if (!prev) return prev;
        const updated = fresh.find((c) => c.id === prev.id);
        return updated ?? null;
      });
    } catch (err) {
      console.error('Failed to refresh collections:', err);
    }
  }, []);

  const handleCreateCollection = useCallback(async () => {
    const name = newCollectionName.trim();
    if (!name) {
      setCreateError('Give your collection a name.');
      return;
    }
    setCreateError(null);
    setCreatingCollection(true);
    try {
      const created = await createCollection(name, newCollectionDesc.trim() || undefined);
      setCollections((prev) => [created, ...prev]);
      setNewCollectionName('');
      setNewCollectionDesc('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create collection';
      setCreateError(msg);
    } finally {
      setCreatingCollection(false);
    }
  }, [newCollectionName, newCollectionDesc]);

  const handleDeleteCollection = useCallback(async (collection: Collection) => {
    const ok = await confirm({
      title: `Delete "${collection.name}"?`,
      message: (
        <>
          The collection will be removed, but the {collection.productCount}{' '}
          product{collection.productCount === 1 ? '' : 's'} inside stay in
          My Products. You can re-group them anytime.
        </>
      ),
      confirmLabel: 'Delete collection',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteCollection(collection.id);
      setCollections((prev) => prev.filter((c) => c.id !== collection.id));
      if (openCollection?.id === collection.id) setOpenCollection(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete collection';
      setFetchError(msg);
    }
  }, [confirm, openCollection?.id]);

  // Open a collection's drill-down view (load its products on-demand).
  const handleOpenCollection = useCallback(async (collection: Collection) => {
    setOpenCollection(collection);
    setOpenCollectionLoading(true);
    setOpenCollectionProducts([]);
    try {
      const items = await listCollectionProducts(collection.id);
      setOpenCollectionProducts(items);
    } catch (err) {
      console.error('Failed to load collection products:', err);
      const msg = err instanceof Error ? err.message : 'Failed to load collection';
      setFetchError(msg);
    } finally {
      setOpenCollectionLoading(false);
    }
  }, []);

  // Modal flow: "Add this product to which collection?"
  const handleAddToCollection = useCallback(async (
    product: UserProduct, collectionId: string,
  ) => {
    try {
      const result = await addProductsToCollection(collectionId, [product.id]);
      if (result.inserted > 0) {
        // Refresh counts + thumbnails in the list view.
        await refreshCollections();
      }
      setAddToTarget(null);
      // Soft confirmation banner — fades into the existing fetchNotice channel.
      if (result.inserted > 0) {
        setFetchNotice(`Added to collection.`);
      } else {
        setFetchNotice(`Already in that collection.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add to collection';
      setFetchError(msg);
    }
  }, [refreshCollections]);

  const handleRemoveFromCollection = useCallback(async (
    collectionId: string, productId: string,
  ) => {
    try {
      await removeProductFromCollection(collectionId, productId);
      setOpenCollectionProducts((prev) => prev.filter((p) => p.id !== productId));
      await refreshCollections();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove from collection';
      setFetchError(msg);
    }
  }, [refreshCollections]);

  // ---------- Derived: search-filtered views ----------

  // Lower-case match across name + description + category + tags.
  // Filter the source list for the active tab so the empty-state +
  // count chip stay in sync with what the user actually sees.
  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const hay = [
        p.name ?? '',
        p.description ?? '',
        p.productCategory ?? '',
        ...(p.tags ?? []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [products, search]);

  const filteredCollections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter((c) => {
      const hay = `${c.name} ${c.description ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [collections, search]);

  // Poll the sync job until terminal. Refresh the products list on
  // EVERY poll (not just terminal) so new products trickle in as the
  // worker completes them. 4s cadence matches the brand-setup poll.
  useEffect(() => {
    if (!syncJobId) return;
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      if (cancelled) return;
      try {
        const [status, fresh] = await Promise.all([
          getProductSaveJobStatus(syncJobId),
          listProducts(),
        ]);
        if (cancelled) return;
        setProducts(fresh);
        const terminal = status.status === 'completed' || status.status === 'failed';
        if (terminal) {
          if (status.status === 'completed') {
            setSyncMessage(
              `Synced ${status.processed_count} product${status.processed_count === 1 ? '' : 's'} from your website.`,
            );
          } else {
            setSyncMessage(
              status.error || 'Sync failed. Some products may not have been added.',
            );
          }
          setSyncing(false);
          setSyncJobId(null);
          return;
        }
      } catch (err) {
        console.error('[sync-more] status poll failed:', err);
      }
      if (!cancelled) timer = window.setTimeout(tick, 4000);
    };
    timer = window.setTimeout(tick, 4000);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [syncJobId]);


  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8 lg:py-12">

        {/* ============ Hero header ============ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8 lg:mb-10">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-shopping-bag-3-line text-2xl" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-1">
                My Products
              </h1>
              <p className="text-dark-muted text-sm lg:text-base sm:max-w-xl">
                Save your products once so you don't have to paste the URL
                every time you create a post.
                {productCap >= 10000
                  ? ' Sync your full catalog.'
                  : ` Up to ${productCap} products.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Sync more from website — re-runs the discover pipeline
                against the active brand's URL and appends only new
                products (dedupe by source_url). Replaces the old
                onboarding flow that only saved the first ~24 picked
                products from the wizard. Hidden once the cap is hit. */}
            {products.length < productCap && (
              <button
                onClick={handleSyncMore}
                disabled={syncing}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                  bg-primary text-white border border-primary
                  hover:bg-primary-hover text-xs font-bold transition-all cursor-pointer
                  disabled:opacity-60 disabled:cursor-not-allowed
                "
                title="Re-scrape your brand's website for more products"
              >
                {syncing ? (
                  <><i className="ri-loader-4-line animate-spin" /> Syncing…</>
                ) : (
                  <><i className="ri-refresh-line" /> Sync more from website</>
                )}
              </button>
            )}
            {products.length > 0 && (
              <button
                onClick={handleClearAll}
                disabled={clearing}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                  bg-white border border-red-200 text-red-600
                  hover:bg-red-50 text-xs font-bold transition-all cursor-pointer
                  disabled:opacity-60 disabled:cursor-not-allowed
                "
                title="Delete every product in your library"
              >
                {clearing ? (
                  <><i className="ri-loader-4-line animate-spin" /> Clearing…</>
                ) : (
                  <><i className="ri-delete-bin-line" /> Clear all</>
                )}
              </button>
            )}
            <span className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
              border border-surface-dark/40 bg-surface text-dark-muted
              text-xs font-semibold
            ">
              {productCap >= 10000
                ? `${products.length} product${products.length === 1 ? '' : 's'}`
                : `${products.length}/${productCap}`}
            </span>
          </div>
        </div>

        {/* Sync status banner — separate from fetchError/fetchNotice so
            the long-running sync feedback doesn't get clobbered by a
            short add-from-URL action that happens during the sync. */}
        {syncMessage && (
          <div
            className={`
              mb-6 rounded-xl border p-3 text-xs flex items-start gap-2
              ${syncing
                ? 'bg-primary/5 border-primary/30 text-primary'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'}
            `}
          >
            <i className={syncing ? 'ri-loader-4-line animate-spin flex-shrink-0 mt-0.5' : 'ri-checkbox-circle-fill flex-shrink-0 mt-0.5'} />
            <span className="flex-1">{syncMessage}</span>
            {!syncing && (
              <button
                onClick={() => setSyncMessage(null)}
                className="text-xs underline opacity-70 hover:opacity-100"
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {initialLoading ? (
          <div className="flex items-center justify-center py-20 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            Loading your products…
          </div>
        ) : (
          <div className="space-y-6">

            {/* ============ Quick start: URL fetch — hoisted ABOVE tabs ============ */}
            {/* June 8: was nested inside the All-Products tab so it
                disappeared on the Collections tab and felt buried. As
                the canonical "primary action" on this page, it belongs
                up top — always visible regardless of which tab the user
                is in. Hidden only inside the collection drill-down view
                (where the add affordance is the modal opened from the
                empty state). */}
            {!openCollection && (() => {
              // Cross-domain soft warning — if the URL the user is trying
              // to add comes from a different host than their active
              // brand's source_url, surface a heads-up. NOT a hard block:
              // dropshippers / resellers / multi-brand users legitimately
              // add third-party products. Just a flag so accidental adds
              // (paste from the wrong tab) get caught.
              const extractHost = (raw: string): string | null => {
                if (!raw) return null;
                try {
                  const withScheme = raw.startsWith('http')
                    ? raw
                    : `https://${raw}`;
                  return new URL(withScheme).hostname.replace(/^www\./, '');
                } catch {
                  return null;
                }
              };
              const urlHost = extractHost(fetchUrl);
              const brandHost = extractHost(activeBrand?.sourceUrl || '');
              const crossDomain =
                urlHost != null && brandHost != null && urlHost !== brandHost;
              return (
                <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                      <i className="ri-link text-base" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-display font-bold text-dark text-base mb-0.5">
                        Add a product
                      </h2>
                      <p className="text-xs text-dark-muted">
                        Paste a single product URL, or a collection / category
                        URL to add several at once. No link? Upload a product
                        image instead.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={fetchUrl}
                      onChange={(e) => setFetchUrl(e.target.value)}
                      placeholder="https://yourstore.com/products/best-seller"
                      disabled={fetching || atCapacity}
                      className="
                        flex-1 px-4 py-2.5 rounded-full bg-surface
                        border border-surface-dark/40 text-sm text-dark
                        placeholder:text-dark-muted/60
                        focus:outline-none focus:border-primary/40
                        disabled:opacity-60 disabled:cursor-not-allowed
                      "
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !fetching && !atCapacity) {
                          handleAddFromUrl();
                        }
                      }}
                    />
                    <button
                      onClick={handleAddFromUrl}
                      disabled={fetching || atCapacity || !fetchUrl.trim()}
                      className="
                        w-full sm:w-auto justify-center
                        inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                        bg-primary hover:bg-primary-hover text-white text-sm font-bold
                        shadow-md shadow-primary/25 transition-all cursor-pointer
                        disabled:opacity-50 disabled:cursor-not-allowed
                      "
                    >
                      {fetching ? (
                        <><i className="ri-loader-4-line animate-spin" />Adding…</>
                      ) : (
                        <><i className="ri-add-line" />Add product</>
                      )}
                    </button>
                  </div>

                  {/* ---- or upload an image ---- */}
                  <div className="flex items-center gap-3 my-3">
                    <div className="h-px flex-1 bg-surface-dark/30" />
                    <span className="text-xs text-dark-muted/70 font-medium">or</span>
                    <div className="h-px flex-1 bg-surface-dark/30" />
                  </div>
                  <label
                    className={`
                      flex items-center justify-center gap-2 px-5 py-2.5 rounded-full
                      bg-surface border border-surface-dark/40 text-sm font-semibold text-dark
                      transition-all
                      ${uploadBusy || atCapacity
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:border-primary/40 hover:text-primary'}
                    `}
                  >
                    {uploadBusy ? (
                      <><i className="ri-loader-4-line animate-spin" />Uploading…</>
                    ) : (
                      <><i className="ri-upload-2-line" />Upload a product image</>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadBusy || atCapacity}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        // Reset the input so re-picking the same file re-fires onChange.
                        e.target.value = '';
                        if (f) handleUploadFile(f);
                      }}
                    />
                  </label>

                  {crossDomain && (
                    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                      <i className="ri-error-warning-line text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Different domain.</strong> This URL ({urlHost})
                        is from a different site than your active brand
                        ({brandHost}). Continue if intended — dropship /
                        reseller products are fine.
                      </span>
                    </div>
                  )}
                  {atCapacity && !fetchError && (
                    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                      <strong>Library is full.</strong> Remove a product below
                      to add a new one.
                    </div>
                  )}
                  {fetchError && (
                    <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
                      <strong>Couldn't add:</strong> {fetchError}
                    </div>
                  )}
                  {fetchNotice && !fetchError && (
                    <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 flex items-start gap-2">
                      <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>{fetchNotice}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ============ Tabs + search ============ */}
            {/* Tab bar — All Products | Collections. Search input on the
                right filters the active view in place. When a collection
                is open (drill-down), hide tabs so the back button is the
                clear way out. */}
            {!openCollection && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-full border border-surface-dark/40 bg-white p-0.5 text-sm font-bold">
                  <button
                    onClick={() => { setActiveTab('products'); setSearch(''); }}
                    className={`
                      px-4 py-1.5 rounded-full transition-all cursor-pointer
                      ${activeTab === 'products'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-dark-muted hover:text-dark'}
                    `}
                  >
                    All Products
                    <span className={`
                      ml-1.5 inline-flex items-center justify-center px-1.5
                      rounded-full text-[10px]
                      ${activeTab === 'products' ? 'bg-white/25 text-white' : 'bg-surface text-dark-muted'}
                    `}>
                      {products.length}
                    </span>
                  </button>
                  <button
                    onClick={() => { setActiveTab('collections'); setSearch(''); }}
                    className={`
                      px-4 py-1.5 rounded-full transition-all cursor-pointer
                      ${activeTab === 'collections'
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-dark-muted hover:text-dark'}
                    `}
                  >
                    Collections
                    <span className={`
                      ml-1.5 inline-flex items-center justify-center px-1.5
                      rounded-full text-[10px]
                      ${activeTab === 'collections' ? 'bg-white/25 text-white' : 'bg-surface text-dark-muted'}
                    `}>
                      {collections.length}
                    </span>
                  </button>
                </div>

                <div className="relative flex-1 min-w-[240px]">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={
                      activeTab === 'products'
                        ? 'Search products by name, category, or tag…'
                        : 'Search collections by name…'
                    }
                    className="
                      w-full pl-9 pr-3 py-2 rounded-full
                      bg-white border border-surface-dark/40
                      text-sm focus:outline-none focus:border-primary
                    "
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark"
                      title="Clear search"
                    >
                      <i className="ri-close-circle-fill text-sm" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ============ Collection drill-down view ============ */}
            {/* Shown when the user clicks a collection card. Back button
                returns to the collection list. Inside: the same 5-up
                product grid as the All Products view, but each card has
                a "Remove from collection" action instead of "Delete". */}
            {openCollection && (
              <CollectionDetailView
                collection={openCollection}
                products={openCollectionProducts}
                loading={openCollectionLoading}
                onBack={() => setOpenCollection(null)}
                onRemove={(pid) => handleRemoveFromCollection(openCollection.id, pid)}
                onDeleteCollection={() => handleDeleteCollection(openCollection)}
                search={search}
                onSearchChange={setSearch}
                columnCount={productColumns}
              />
            )}

            {/* ============ Collections list view ============ */}
            {!openCollection && activeTab === 'collections' && (
              <CollectionsListView
                collections={filteredCollections}
                loading={collectionsLoading}
                allCount={collections.length}
                onOpen={handleOpenCollection}
                onDelete={handleDeleteCollection}
                newCollectionName={newCollectionName}
                onNewCollectionName={setNewCollectionName}
                newCollectionDesc={newCollectionDesc}
                onNewCollectionDesc={setNewCollectionDesc}
                creating={creatingCollection}
                createError={createError}
                onCreate={handleCreateCollection}
                searchActive={search.trim().length > 0}
              />
            )}

            {/* ============ All-Products view (default) ============ */}
            {!openCollection && activeTab === 'products' && (
            <>
            {/* URL-fetch panel hoisted above the tabs — see top of this
                file's main render. */}

            {/* ============ Saved-products grid ============ */}
            {/* products.length === 0 → empty-state card. With products
                but no search hits → "No matches" tip. Otherwise → 5-up
                grid (user spec). items-start keeps same-row cards
                top-aligned; uniform tile height is enforced by the
                aspect-square image area + min-h on the description. */}
            {products.length === 0 ? (
              <div className="
                bg-white rounded-2xl border-2 border-dashed border-surface-dark/40
                p-10 text-center
              ">
                <div className="w-12 h-12 rounded-2xl bg-surface mx-auto mb-3 flex items-center justify-center text-dark-muted">
                  <i className="ri-shopping-bag-3-line text-2xl" />
                </div>
                <p className="text-dark font-semibold text-base mb-1">
                  No products yet
                </p>
                <p className="text-dark-muted text-sm">
                  Paste a product URL above to add your first one.
                </p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 text-dark-muted text-sm">
                No products match "{search}". <button
                  className="underline text-primary"
                  onClick={() => setSearch('')}
                >Clear search</button>
              </div>
            ) : (() => {
              // Round-robin distribute into N flex columns — every card
              // packs tightly against its previous sibling in the same
              // column. No more "tallest tile defines row height + leaves
              // empty space below shorter ones" issue.
              const cols: UserProduct[][] = Array.from(
                { length: productColumns }, () => [],
              );
              filteredProducts.forEach((p, idx) => {
                cols[idx % productColumns].push(p);
              });
              return (
                <div className="flex gap-3 lg:gap-4 items-start">
                  {cols.map((col, colIdx) => (
                    <div
                      key={`pcol-${colIdx}`}
                      className="flex-1 min-w-0 flex flex-col gap-3 lg:gap-4"
                    >
                      {col.map((p) => (
                        <ProductCard
                          key={p.id}
                          product={p}
                          deleting={deletingId === p.id}
                          onDelete={() => handleDelete(p.id)}
                          onAddToCollection={() => setAddToTarget(p)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}
            </>
            )}

          </div>
        )}
      </div>

      {/* "Add to which collection?" modal — opens when user clicks the
          bookmark icon on a product card. Lets them pick an existing
          collection or create a new one inline. */}
      {addToTarget && (
        <AddToCollectionModal
          product={addToTarget}
          collections={collections}
          onClose={() => setAddToTarget(null)}
          onPick={(cid) => handleAddToCollection(addToTarget, cid)}
          onCreateAndAdd={async (name) => {
            try {
              const created = await createCollection(name);
              setCollections((prev) => [created, ...prev]);
              await handleAddToCollection(addToTarget, created.id);
            } catch (err) {
              const msg = err instanceof Error ? err.message : 'Failed to create collection';
              setFetchError(msg);
            }
          }}
        />
      )}

      {/* ---- Manual product info modal (upload fallback) ----
          Shown when an uploaded image couldn't be auto-classified. The user
          confirms a name + short description; we re-submit the same file. */}
      {manualUpload && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-surface-dark/30 shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-surface border border-surface-dark/40 flex-shrink-0">
                <img
                  src={manualUpload.previewUrl}
                  alt="Product preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h2 className="font-display font-bold text-dark text-lg mb-1">
                  Tell us about this product
                </h2>
                <p className="text-xs text-dark-muted leading-relaxed">
                  We couldn't read the details from the image. Add a name and a
                  short description so we can generate great ads for it.
                </p>
              </div>
            </div>

            <label className="block text-xs font-semibold text-dark mb-1.5">
              Product name
            </label>
            <input
              type="text"
              value={manualUpload.name}
              onChange={(e) =>
                setManualUpload((m) => (m ? { ...m, name: e.target.value } : m))
              }
              placeholder="e.g. Radiance Vitamin C Serum"
              disabled={manualUpload.saving}
              className="
                w-full px-4 py-2.5 rounded-xl bg-surface border border-surface-dark/40
                text-sm text-dark placeholder:text-dark-muted/60 mb-4
                focus:outline-none focus:border-primary/40
                disabled:opacity-60
              "
            />

            <label className="block text-xs font-semibold text-dark mb-1.5">
              Short description
            </label>
            <textarea
              value={manualUpload.description}
              onChange={(e) =>
                setManualUpload((m) =>
                  m ? { ...m, description: e.target.value } : m,
                )
              }
              placeholder="A few words on what it is and who it's for."
              rows={3}
              disabled={manualUpload.saving}
              className="
                w-full px-4 py-2.5 rounded-xl bg-surface border border-surface-dark/40
                text-sm text-dark placeholder:text-dark-muted/60 resize-none
                focus:outline-none focus:border-primary/40
                disabled:opacity-60
              "
            />

            {manualUpload.error && (
              <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
                {manualUpload.error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={cancelManualUpload}
                disabled={manualUpload.saving}
                className="px-4 py-2.5 rounded-full text-sm font-semibold text-dark-muted hover:text-dark transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitManualUpload}
                disabled={
                  manualUpload.saving ||
                  !manualUpload.name.trim() ||
                  !manualUpload.description.trim()
                }
                className="
                  inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                  bg-primary hover:bg-primary-hover text-white text-sm font-bold
                  shadow-md shadow-primary/25 transition-all cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {manualUpload.saving ? (
                  <><i className="ri-loader-4-line animate-spin" />Saving…</>
                ) : (
                  <><i className="ri-add-line" />Add product</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayoutV2>
  );
}


// =============================================================================
// ProductCard — one tile in the saved-products grid. Image on top,
// product name + short description below, hover-reveal delete button.
// =============================================================================

function ProductCard({
  product,
  deleting,
  onDelete,
  onAddToCollection,
  onRemoveFromCollection,
}: {
  product: UserProduct;
  deleting: boolean;
  onDelete?: () => void;
  /** Bookmark icon — opens the "Add to which collection?" modal. */
  onAddToCollection?: () => void;
  /** Used inside CollectionDetailView — swaps the delete button for a
   *  "remove from this collection" button (doesn't delete the product). */
  onRemoveFromCollection?: () => void;
}) {
  return (
    <div
      className="
        group relative bg-white rounded-2xl border border-surface-dark/30
        overflow-hidden flex flex-col
        hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10
        transition-all
      "
    >
      {/* Image — square aspect, contained (don't crop the product).
          bg-white so transparent / off-white product photos blend into
          the card instead of showing the page's grey surface color
          around the image edges. */}
      <div className="relative aspect-square bg-white flex items-center justify-center">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name || 'Product'}
            className="w-full h-full object-contain p-3"
          />
        ) : (
          <i className="ri-image-line text-4xl text-dark-muted/40" />
        )}
        {/* "No Recipe" — the classifier couldn't map this product to any
            recipe-eligible category, so it was parked as `none` and its ads
            render freestyle (no recipe). Surfaced so it's visibly captured,
            not silently unclassified. */}
        {product.productCategory === 'none' && (
          <span
            title="Didn't match any recipe category — this product renders freestyle (no recipe)"
            className="
              absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full
              bg-amber-100 text-amber-700 text-xs font-semibold
              border border-amber-200
            "
          >
            No Recipe
          </span>
        )}
        {/* "Classifying…" — Jul 26. Category null/empty means the classifier
            never ran on this row yet (distinct from 'none' above). It has no
            type anchor, so it's not yet usable in a batch; the background
            classifier + scheduled sweep will fill it in shortly. */}
        {!product.productCategory && (
          <span
            title="We're still classifying this product — it'll be ready to use shortly."
            className="
              absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full
              bg-dark/70 text-white text-xs font-semibold
              inline-flex items-center gap-1
            "
          >
            <i className="ri-loader-4-line animate-spin text-[11px]" />
            Classifying…
          </span>
        )}
        {/* Permanent "+ Add to collection" button — visible on mobile
            (hover doesn't exist on touch) AND on desktop. Anchored
            bottom-right of the image. Hidden when this card represents
            an already-in-collection item (CollectionDetailView passes
            onRemoveFromCollection instead). */}
        {onAddToCollection && (
          <button
            onClick={onAddToCollection}
            title="Add to collection"
            aria-label="Add to collection"
            className="
              absolute bottom-2 right-2 z-10
              w-9 h-9 rounded-full
              bg-primary text-white shadow-md shadow-primary/30
              flex items-center justify-center
              hover:bg-primary-hover transition-all cursor-pointer
            "
          >
            <i className="ri-add-line text-base" />
          </button>
        )}
        {/* Hover-revealed action button cluster — top-right. Delete +
            (in CollectionDetailView) remove-from-collection. The add-to-
            collection action lives in the always-visible bottom-right
            button above so mobile + power users get one consistent UX. */}
        <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
          {onRemoveFromCollection && (
            <button
              onClick={onRemoveFromCollection}
              title="Remove from this collection"
              className="
                w-8 h-8 rounded-full
                bg-white/90 backdrop-blur-sm border border-surface-dark/40
                flex items-center justify-center text-amber-600 hover:text-white hover:bg-amber-500
                cursor-pointer shadow-sm
              "
            >
              <i className="ri-subtract-line text-sm" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              disabled={deleting}
              title="Delete this product"
              className="
                w-8 h-8 rounded-full
                bg-white/90 backdrop-blur-sm border border-surface-dark/40
                flex items-center justify-center text-red-500 hover:text-white hover:bg-red-500
                disabled:opacity-100 disabled:cursor-not-allowed cursor-pointer
                shadow-sm
              "
            >
              {deleting ? (
                <i className="ri-loader-4-line animate-spin text-sm" />
              ) : (
                <i className="ri-delete-bin-line text-sm" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Text block — name (bold, 1-line clamp) + description (muted,
          2-line clamp). 5-up grid means tiles are narrow at lg+; keeping
          the text block tight prevents lopsided tile heights between
          short-name + long-description vs the reverse. min-h reserves
          the space so a name-only tile is still the same height as one
          with a 2-line description. */}
      <div className="p-3 flex-1 flex flex-col gap-1">
        <h3
          className="font-display font-bold text-dark text-sm leading-snug truncate"
          title={product.name ?? undefined}
        >
          {product.name || 'Untitled product'}
        </h3>
        {product.description ? (
          <p
            className="text-dark-muted text-xs leading-relaxed min-h-[2.4em]"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={product.description}
          >
            {product.description}
          </p>
        ) : (
          <p className="text-dark-muted/60 text-xs italic min-h-[2.4em]">
            No description
          </p>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// CollectionsListView — grid of collection cards + inline create-new card.
// Shown on the Collections tab when no collection is drilled into.
// =============================================================================

function CollectionsListView({
  collections,
  loading,
  allCount,
  onOpen,
  onDelete,
  newCollectionName,
  onNewCollectionName,
  newCollectionDesc,
  onNewCollectionDesc,
  creating,
  createError,
  onCreate,
  searchActive,
}: {
  collections: Collection[];
  loading: boolean;
  allCount: number;
  onOpen: (c: Collection) => void;
  onDelete: (c: Collection) => void;
  newCollectionName: string;
  onNewCollectionName: (v: string) => void;
  newCollectionDesc: string;
  onNewCollectionDesc: (v: string) => void;
  creating: boolean;
  createError: string | null;
  onCreate: () => void;
  searchActive: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Create-new card — inline form so users can spin up collections
          without leaving the page. Single line for name + optional
          description, single Create button. */}
      <div className="bg-white rounded-2xl border-2 border-surface-dark/30 p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <i className="ri-folder-add-line text-base" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-bold text-dark text-base mb-0.5">
              Create a new collection
            </h2>
            <p className="text-xs text-dark-muted">
              Group products together for easier classification and to run
              generation flows scoped to that subset.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newCollectionName}
            onChange={(e) => onNewCollectionName(e.target.value)}
            placeholder="Name (e.g. Spring serums, Bestsellers)"
            disabled={creating}
            onKeyDown={(e) => { if (e.key === 'Enter' && !creating) onCreate(); }}
            className="
              flex-1 px-4 py-2.5 rounded-full border border-surface-dark/40
              text-sm focus:outline-none focus:border-primary
              disabled:bg-surface
            "
            maxLength={80}
          />
          <input
            type="text"
            value={newCollectionDesc}
            onChange={(e) => onNewCollectionDesc(e.target.value)}
            placeholder="Short description (optional)"
            disabled={creating}
            onKeyDown={(e) => { if (e.key === 'Enter' && !creating) onCreate(); }}
            className="
              flex-1 px-4 py-2.5 rounded-full border border-surface-dark/40
              text-sm focus:outline-none focus:border-primary
              disabled:bg-surface
            "
            maxLength={400}
          />
          <button
            onClick={onCreate}
            disabled={creating || !newCollectionName.trim()}
            className="
              inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full
              bg-primary text-white hover:bg-primary-hover text-sm font-bold
              shadow-md transition-all cursor-pointer
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          >
            {creating ? (
              <><i className="ri-loader-4-line animate-spin" /> Creating…</>
            ) : (
              <><i className="ri-add-line" /> Create</>
            )}
          </button>
        </div>
        {createError && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">
            {createError}
          </div>
        )}
      </div>

      {/* Collections grid OR empty state */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-dark-muted">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Loading collections…
        </div>
      ) : allCount === 0 ? (
        <div className="
          bg-white rounded-2xl border-2 border-dashed border-surface-dark/40
          p-10 text-center
        ">
          <div className="w-12 h-12 rounded-2xl bg-surface mx-auto mb-3 flex items-center justify-center text-dark-muted">
            <i className="ri-folder-3-line text-2xl" />
          </div>
          <p className="text-dark font-semibold text-base mb-1">
            No collections yet
          </p>
          <p className="text-dark-muted text-sm">
            Create your first collection above, then add products to it from
            the All Products tab.
          </p>
        </div>
      ) : collections.length === 0 ? (
        <div className="text-center py-12 text-dark-muted text-sm">
          No collections match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {collections.map((c) => (
            <CollectionCard
              key={c.id}
              collection={c}
              onOpen={() => onOpen(c)}
              onDelete={() => onDelete(c)}
            />
          ))}
        </div>
      )}
      {searchActive && allCount > 0 && collections.length > 0 && (
        <p className="text-xs text-dark-muted text-center">
          Showing {collections.length} of {allCount} collections.
        </p>
      )}
    </div>
  );
}


// =============================================================================
// CollectionCard — one tile on the Collections grid. Shows the name +
// description + product count + a 2×2 mosaic of the first 4 thumbnails.
// =============================================================================

function CollectionCard({
  collection,
  onOpen,
  onDelete,
}: {
  collection: Collection;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const thumbs = collection.thumbnails.slice(0, 4);
  return (
    <button
      onClick={onOpen}
      className="
        group relative bg-white rounded-2xl border border-surface-dark/30
        overflow-hidden flex flex-col text-left
        hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10
        transition-all cursor-pointer
      "
    >
      {/* 2×2 thumbnail mosaic — uses object-cover here (not contain) so
          the small previews fill cleanly. The detail view shows the full
          fit-to-square treatment. Empty slots get a neutral placeholder. */}
      <div className="relative aspect-square bg-surface grid grid-cols-2 grid-rows-2 gap-0.5 p-0.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white flex items-center justify-center overflow-hidden">
            {thumbs[i] ? (
              <img
                src={thumbs[i]}
                alt=""
                className="w-full h-full object-contain p-1"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <i className="ri-image-line text-dark-muted/30 text-xl" />
            )}
          </div>
        ))}
        {/* Hover-revealed delete on the card */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete this collection"
          className="
            absolute top-2 right-2 w-8 h-8 rounded-full
            bg-white/90 backdrop-blur-sm border border-surface-dark/40
            flex items-center justify-center text-red-500 hover:text-white hover:bg-red-500
            opacity-0 group-hover:opacity-100 transition-all cursor-pointer shadow-sm
          "
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </div>

      <div className="p-3 flex-1 flex flex-col gap-1">
        <h3 className="font-display font-bold text-dark text-sm leading-snug truncate">
          {collection.name}
        </h3>
        <p className="text-dark-muted text-xs">
          {collection.productCount} product{collection.productCount === 1 ? '' : 's'}
        </p>
        {collection.description && (
          <p
            className="text-dark-muted text-xs leading-relaxed"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={collection.description}
          >
            {collection.description}
          </p>
        )}
      </div>
    </button>
  );
}


// =============================================================================
// CollectionDetailView — drill-down for a single collection. Header
// (back / name / count / delete) + 5-up product grid with "remove from
// collection" actions (not delete-product).
// =============================================================================

function CollectionDetailView({
  collection,
  products,
  loading,
  onBack,
  onRemove,
  onDeleteCollection,
  search,
  onSearchChange,
  columnCount,
}: {
  collection: Collection;
  products: UserProduct[];
  loading: boolean;
  onBack: () => void;
  onRemove: (productId: string) => void;
  onDeleteCollection: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  columnCount: number;
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const hay = [
        p.name ?? '',
        p.description ?? '',
        p.productCategory ?? '',
        ...(p.tags ?? []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [products, search]);

  return (
    <div className="space-y-6">
      {/* Header bar — back button + name + count + delete on the right */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onBack}
          className="
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
            bg-white border border-surface-dark/40 text-dark
            hover:border-primary/40 text-xs font-bold transition-all cursor-pointer
          "
        >
          <i className="ri-arrow-left-line" /> All collections
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-dark text-lg truncate">
            {collection.name}
          </h2>
          {collection.description && (
            <p className="text-dark-muted text-xs truncate">{collection.description}</p>
          )}
        </div>
        <span className="
          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
          border border-surface-dark/40 bg-surface text-dark-muted text-xs font-semibold
        ">
          {collection.productCount} product{collection.productCount === 1 ? '' : 's'}
        </span>
        <button
          onClick={onDeleteCollection}
          className="
            inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
            bg-white border border-red-200 text-red-600 hover:bg-red-50
            text-xs font-bold transition-all cursor-pointer
          "
          title="Delete this collection (products stay in My Products)"
        >
          <i className="ri-delete-bin-line" /> Delete collection
        </button>
      </div>

      {/* Search inside the collection */}
      <div className="relative max-w-[480px]">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search products in this collection…"
          className="
            w-full pl-9 pr-3 py-2 rounded-full
            bg-white border border-surface-dark/40
            text-sm focus:outline-none focus:border-primary
          "
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-dark"
          >
            <i className="ri-close-circle-fill text-sm" />
          </button>
        )}
      </div>

      {/* Body — loading / empty / grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-dark-muted">
          <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
          Loading…
        </div>
      ) : products.length === 0 ? (
        <div className="
          bg-white rounded-2xl border-2 border-dashed border-surface-dark/40
          p-10 text-center
        ">
          <div className="w-12 h-12 rounded-2xl bg-surface mx-auto mb-3 flex items-center justify-center text-dark-muted">
            <i className="ri-folder-line text-2xl" />
          </div>
          <p className="text-dark font-semibold text-base mb-1">
            No products in this collection yet
          </p>
          <p className="text-dark-muted text-sm">
            Switch to the All Products tab and click the bookmark icon on
            any product to add it here.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-dark-muted text-sm">
          No matches for "{search}". <button
            className="underline text-primary"
            onClick={() => onSearchChange('')}
          >Clear</button>
        </div>
      ) : (() => {
        const cols: UserProduct[][] = Array.from({ length: columnCount }, () => []);
        filtered.forEach((p, idx) => { cols[idx % columnCount].push(p); });
        return (
          <div className="flex gap-3 lg:gap-4 items-start">
            {cols.map((col, colIdx) => (
              <div
                key={`pcol-${colIdx}`}
                className="flex-1 min-w-0 flex flex-col gap-3 lg:gap-4"
              >
                {col.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    deleting={false}
                    onRemoveFromCollection={() => onRemove(p.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}


// =============================================================================
// AddToCollectionModal — picker UI shown when a user clicks the
// bookmark icon on a product. Lists existing collections + an inline
// "Create new" affordance.
// =============================================================================

function AddToCollectionModal({
  product,
  collections,
  onClose,
  onPick,
  onCreateAndAdd,
}: {
  product: UserProduct;
  collections: Collection[];
  onClose: () => void;
  onPick: (collectionId: string) => void;
  onCreateAndAdd: (name: string) => Promise<void>;
}) {
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter((c) => c.name.toLowerCase().includes(q));
  }, [collections, search]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      await onCreateAndAdd(name);
      // onCreateAndAdd handles closing via state in parent.
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-surface-dark/40 max-w-md w-full max-h-[80vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-surface-dark/30 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-surface flex-shrink-0 overflow-hidden">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt="" className="w-full h-full object-contain" />
            ) : (
              <i className="ri-image-line text-dark-muted" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-dark text-base truncate">
              Add to which collection?
            </h3>
            <p className="text-xs text-dark-muted truncate">
              {product.name || 'Untitled product'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-dark-muted hover:text-dark"
            title="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Existing collections — scrollable list */}
        <div className="p-3 border-b border-surface-dark/30">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search your collections…"
              className="
                w-full pl-9 pr-3 py-2 rounded-full
                bg-surface border border-surface-dark/40
                text-sm focus:outline-none focus:border-primary focus:bg-white
              "
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {collections.length === 0 ? (
            <p className="text-center text-dark-muted text-sm py-6">
              You don't have any collections yet. Create one below.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-dark-muted text-sm py-6">
              No collections match "{search}".
            </p>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => onPick(c.id)}
                className="
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                  hover:bg-primary/5 text-left cursor-pointer transition-all
                "
              >
                <div className="w-8 h-8 rounded-lg bg-surface flex-shrink-0 flex items-center justify-center text-primary">
                  <i className="ri-folder-3-line" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-dark truncate">{c.name}</p>
                  <p className="text-xs text-dark-muted">
                    {c.productCount} product{c.productCount === 1 ? '' : 's'}
                  </p>
                </div>
                <i className="ri-add-circle-line text-primary text-lg" />
              </button>
            ))
          )}
        </div>

        {/* Create-new row — single input + button, lives in the footer */}
        <div className="p-4 border-t border-surface-dark/30 bg-surface/50">
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="+ Create new collection"
              disabled={creating}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              className="
                flex-1 px-4 py-2 rounded-full border border-surface-dark/40
                bg-white text-sm focus:outline-none focus:border-primary
                disabled:opacity-60
              "
              maxLength={80}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="
                inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                bg-primary text-white hover:bg-primary-hover text-sm font-bold
                disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer
              "
            >
              {creating ? <i className="ri-loader-4-line animate-spin" /> : 'Create + Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

